// Malphite Unstoppable Force: Malphite dashes to the enemy team, crashes into them,
// then a shockwave expands outward. Phases: DASH → CRASH → SHOCKWAVE.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";

const DASH_MS      = 300;
const CRASH_MS     = 200;
const SHOCKWAVE_MS = 380;
const TOTAL        = DASH_MS + CRASH_MS + SHOCKWAVE_MS;
const IMPACT_START = DASH_MS + CRASH_MS;

const DEBRIS = Array.from({ length: 14 }, (_, i) => ({
  angle: (i / 14) * Math.PI * 2 + 0.22,
  speed: 0.6 + (i % 3) * 0.25,
  size:  2 + (i % 4),
  color: i % 2 === 0 ? "#8a7a68" : "#5a4a38",
}));

export class MalphiteUltDashVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  onEnd?: () => void;

  constructor(
    private visual: UnitVisual,
    private startPx: Point,
    private landingPx: Point,
    private hexSize: number,
    private onImpact: () => void,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);

    // Slide Malphite's sprite to landing zone during dash phase
    const dashT = Math.min(this.elapsed / DASH_MS, 1);
    if (dashT > 0) {
      const eased = Easing.easeInOutCubic(dashT);
      this.visual.x = this.startPx.x + (this.landingPx.x - this.startPx.x) * eased;
      this.visual.y = this.startPx.y + (this.landingPx.y - this.startPx.y) * eased;
    }

    if (!this.impactFired && this.elapsed >= IMPACT_START) {
      this.impactFired = true;
      this.onImpact();
    }

    if (this.elapsed >= TOTAL) this.onEnd?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;
    if (e < DASH_MS) {
      this.drawDashTrail(ctx, e / DASH_MS);
    } else if (e < IMPACT_START) {
      this.drawCrash(ctx, (e - DASH_MS) / CRASH_MS);
    } else {
      this.drawShockwave(ctx, (e - IMPACT_START) / SHOCKWAVE_MS);
    }
  }

  private drawDashTrail(ctx: CanvasRenderingContext2D, t: number): void {
    const curX = this.visual.x;
    const curY = this.visual.y;
    const dx = curX - this.startPx.x;
    const dy = curY - this.startPx.y;
    const trailLen = Math.sqrt(dx * dx + dy * dy);
    if (trailLen < 2) return;

    const angle = Math.atan2(dy, dx);
    const fade = 1 - t * 0.5;

    ctx.save();
    ctx.translate(this.startPx.x, this.startPx.y);
    ctx.rotate(angle);

    // Rock dust trail — wide glow
    ctx.globalAlpha = fade * 0.4;
    ctx.fillStyle = "#8a7a68";
    ctx.shadowColor = "#555533";
    ctx.shadowBlur = 20;
    ctx.fillRect(0, -this.hexSize * 0.55, trailLen, this.hexSize * 1.1);

    // Bright core streak
    ctx.globalAlpha = fade * 0.8;
    ctx.fillStyle = "#c8b898";
    ctx.shadowBlur = 8;
    ctx.fillRect(0, -this.hexSize * 0.18, trailLen, this.hexSize * 0.36);

    // Purple tint (magic) edge lines
    ctx.globalAlpha = fade * 0.55;
    ctx.strokeStyle = "#9944cc";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#6633aa";
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.moveTo(0, -this.hexSize * 0.42);
    ctx.lineTo(trailLen, -this.hexSize * 0.42);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, this.hexSize * 0.42);
    ctx.lineTo(trailLen, this.hexSize * 0.42);
    ctx.stroke();

    ctx.restore();
  }

  private drawCrash(ctx: CanvasRenderingContext2D, t: number): void {
    const cx = this.landingPx.x;
    const cy = this.landingPx.y;
    const r = this.hexSize * 0.9;

    ctx.save();

    // Growing shadow on ground
    ctx.globalAlpha = Easing.easeOutQuad(t) * 0.6;
    ctx.fillStyle = "#1a1206";
    ctx.shadowColor = "#555533";
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.ellipse(cx, cy + 10, r * Easing.easeOutQuad(t), r * 0.45 * Easing.easeOutQuad(t), 0, 0, Math.PI * 2);
    ctx.fill();

    // Impact flash
    if (t > 0.6) {
      const flash = (t - 0.6) / 0.4;
      ctx.globalAlpha = (1 - flash) * 0.7;
      ctx.fillStyle = "#aaaacc";
      ctx.shadowColor = "#6644cc";
      ctx.shadowBlur = 30;
      ctx.beginPath();
      ctx.ellipse(cx, cy + 8, r * 1.2, r * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private drawShockwave(ctx: CanvasRenderingContext2D, t: number): void {
    const fade = 1 - Easing.easeInQuad(Math.min(t, 1));
    const cx = this.landingPx.x;
    const cy = this.landingPx.y;

    ctx.save();

    // Ground flash immediately after impact
    if (t < 0.2) {
      ctx.globalAlpha = (1 - t / 0.2) * 0.45;
      ctx.fillStyle = "#aaaacc";
      ctx.shadowColor = "#6644cc";
      ctx.shadowBlur = 24;
      ctx.beginPath();
      ctx.ellipse(cx, cy + 8, this.hexSize * 0.8, this.hexSize * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Main shockwave ring
    const ring1 = this.hexSize * 0.5 + t * this.hexSize * 2.2;
    ctx.globalAlpha = fade * 0.75;
    ctx.strokeStyle = "#aaaacc";
    ctx.lineWidth = 4 - t * 2;
    ctx.shadowColor = "#6633cc";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.arc(cx, cy, ring1, 0, Math.PI * 2);
    ctx.stroke();

    // Secondary ring
    const ring2 = this.hexSize * 0.3 + t * this.hexSize * 1.4;
    ctx.globalAlpha = fade * 0.5;
    ctx.strokeStyle = "#ccccdd";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, ring2, 0, Math.PI * 2);
    ctx.stroke();

    // Debris rocks flying out
    if (t < 0.55) {
      const debrisT = t / 0.55;
      for (const d of DEBRIS) {
        const dist = this.hexSize * 0.3 + this.hexSize * 1.4 * Easing.easeOutQuad(debrisT) * d.speed;
        const px = cx + Math.cos(d.angle) * dist;
        const py = cy + Math.sin(d.angle) * dist;
        ctx.globalAlpha = fade * 0.85;
        ctx.fillStyle = d.color;
        ctx.shadowBlur = 4;
        ctx.beginPath();
        ctx.arc(px, py, d.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
